import React, { useLayoutEffect, useState } from 'react';
import { FailedPayment, Loading, PaymentForm, PaymentFormValues, ReceiptTotalTitle, ReservationDetails } from '../../components';
import { useSelector } from 'react-redux';
import { StoreState } from '../../types/store.type';
import { Guests } from 'guest-data-types-ts-lib/index';
import { PaymentUtils, ReservationUtils, SharedUtils, TranslationUtils } from '../../utils';
import ddLogger from '../../logging/ddLogger';
import { TripService } from '../../services/trip/tripService';
import { useIntl } from 'react-intl';
import { CookieKeys } from '../../types/keys.type';
import { Currency } from '../../types/preference.type';
import { PaymentAmount } from 'guest-data-types-ts-lib';
import { AppRoutes } from '../../routes';
import { useHistory } from 'react-router-dom';
import { useTripStoreUpdater } from '../../hooks';
import { getCardType } from '../../utils/creditCardValidation.utils';

export const Payment: React.FC = () => {
    useTripStoreUpdater(true);
    const intl = useIntl();
    const history = useHistory();
    const [didPaymentFailed, setDidPaymentFailed] = useState<boolean>(false);
    const { trip } = useSelector((store: StoreState) => store);
    const { guests, stay, reservation, home_info, unit } = trip;
    let currency = SharedUtils.getCookie(CookieKeys.PREFERRED_CURRENCY) as any;
    currency = currency || Currency.USD;

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    });

    if (trip.isLoading) {
        return <Loading />;
    }

    if (!guests || !stay || !reservation || !home_info || !unit) {
        return null;
    }

    if (reservation.balance <= 0 || ReservationUtils.shouldIgnoreBalance(reservation)) {
        history.push(AppRoutes.RESERVATION);
    }

    const payBalance = async (reservationId: string, values: PaymentFormValues, guests: Guests) => {
        const paymentAmount: PaymentAmount = {
            total: reservation.balance,
            currency: reservation.booked_currency_code || currency,
        };
        const payBalanceRequest = PaymentUtils.transformToPayBalance(guests.primary, paymentAmount, values);
        const { type: cardType } = getCardType(payBalanceRequest.paymentInstrument.creditCard.cardNumber);
        try {
            ddLogger.info(`pay balance for ${reservationId} with ${cardType} credit card`, { reservation_id: reservationId });
            const tripService = new TripService();
            await tripService.payBalance(reservationId, payBalanceRequest);
            ddLogger.info(`pay balance for ${reservationId} successfully paid`, {
                reservation_id: reservationId,
                amount: paymentAmount.total,
                currency: paymentAmount.currency,
            });
            history.push({
                pathname: AppRoutes.PAYMENT_THANK_YOU,
                state: { isSuccessful: true },
            });
        } catch (e) {
            const code = e.code;
            ddLogger.error(`ERROR pay balance for ${reservationId}`, {
                error: e.message,
                reservation_id: reservationId,
                amount: paymentAmount.total,
                currency: paymentAmount.currency,
                code,
            });
            setDidPaymentFailed(true);
        }
    };

    return (
        <div>
            <h1 tabIndex={0} className="heading-large page-title">
                {TranslationUtils.get('make_payment')}
            </h1>
            <div className={'row'}>
                <div className={'col-12 col-md-7'}>
                    {didPaymentFailed && <FailedPayment />}
                    <div className={'d-md-none'}>
                        <div className={'type-body-small reservation-details-mobile px-16 py-24'}>
                            <ReservationDetails preview={null} />
                        </div>
                    </div>
                    {!didPaymentFailed && (
                        <React.Fragment>
                            <div className="d-none d-md-block p-2">
                                <ReceiptTotalTitle
                                    summary={{
                                        total: reservation.balance,
                                        currency: reservation.booked_currency_code || '',
                                    }}
                                    convertedSummary={{
                                        total: reservation.converted_amount?.balance || 0,
                                        currency: reservation.converted_amount?.currency || currency,
                                        conversion_rate: reservation.converted_amount?.conversion_rate,
                                    }}
                                    locale={intl.locale}
                                />
                            </div>
                            <PaymentForm
                                guests={guests}
                                currency={(reservation.booked_currency_code || currency) as Currency}
                                onSubmit={(values) => {
                                    return payBalance(reservation.id, values, guests);
                                }}
                            />
                        </React.Fragment>
                    )}
                </div>
                <div className={'d-none d-md-block col-md-5'}>
                    <ReservationDetails preview={null} />
                </div>
            </div>
        </div>
    );
};
